import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../lib/api';
import { MFA_WINDOW_MS, useAuth } from '../lib/auth';
import { visitorId } from '../lib/track';
import { ErrorBox } from '../ui/kit';
import { Logo } from '../ui/logo';
import { refusedLoginLink } from '../lib/email-login';
import { BILLING_COUNTRIES, countryCode } from '../lib/countries';
import { pickedPlan, planFromUrl } from '../lib/picked-plan';

// The plan chosen on outlevo.com ("Start" on Builder or Growth, /signup?plan=builder) is kept before sign-in and
// shown as a strip once the workspace exists (ui/plan-picked.tsx); Billing opens with that plan highlighted.
planFromUrl();

// Sign-in as on the coachcouples platform: Google on Outlevo's own domain, or a one-time code that Outlevo emails
// (the email's button opens this page with the code filled in, so it works in any browser). No passwords. TOTP is
// optional for sign-in and required (within 12 h) for approvals.

const ERRORS: Record<string, string> = {
  google_cancelled: 'Google sign-in was cancelled.', google_expired: 'That took too long. Start again.',
  google_unverified: 'Google did not confirm that email address. Use the email code instead.', google_failed: 'Google sign-in did not work. Try again or use the email code.',
};
const safeNext = (n: string | null) => (n && n.startsWith('/') && !n.startsWith('//') ? n : '/');

export function Login() {
  const { supabase } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [cfg, setCfg] = useState<{ email_code: boolean; google: boolean } | null>(null);
  const [email, setEmail] = useState(params.get('email') ?? '');
  const [code, setCode] = useState('');
  const [step, setStep] = useState<'email' | 'code' | 'auto'>('email');
  const [busy, setBusy] = useState(false);
  // a sign-in link from an email that was used already or expired: said once, instead of a silent sign-in form
  const [err, setErr] = useState<string | null>(params.get('error') ? ERRORS[params.get('error')!] ?? 'Sign-in did not work. Try again.' : refusedLoginLink() ? 'That sign-in link was used already or has expired. Enter your email for a new code.' : null);
  // arriving from the free assessment: it is kept and becomes the ICP draft after sign-in (it was a generic page)
  const fromAssessment = (() => { try { return Boolean(localStorage.getItem('olv_assessment')); } catch { return false; } })();
  // people who press "Start" on outlevo.com are new: the page says "Create your account" (the same code signs in a returning person)
  const arriving = fromAssessment || Boolean(pickedPlan());
  const [resendIn, setResendIn] = useState(0);
  // the page that sent people here to sign in (the Gmail extension's /connect/gmail, a link to /ad-campaigns) is where
  // they return to, by email code or Google (Google came back to Home)
  const signup = /^\/signup\/?$/.test(location.pathname);
  const next = safeNext(params.get('next') ?? (!/^\/(login|signup)?\/?$/.test(location.pathname) ? location.pathname + location.search : null));
  // the tab says what the heading says (it read "Outlevo · Create your account" under "Create your Outlevo account")
  useEffect(() => { document.title = signup || arriving ? 'Create your Outlevo account' : 'Sign in to Outlevo'; }, [signup, arriving]);
  useEffect(() => {
    const on = () => { refusedLoginLink(); setErr('That sign-in link was used already or has expired. Enter your email for a new code.'); };
    window.addEventListener('olv-login-link-refused', on);
    return () => window.removeEventListener('olv-login-link-refused', on);
  }, []);
  useEffect(() => { api<{ sign_in?: { email_code: boolean; google: boolean } }>('/public/portal-config').then((c) => setCfg(c.sign_in ?? { email_code: false, google: false })).catch(() => setCfg({ email_code: false, google: false })); }, []);
  useEffect(() => { if (resendIn <= 0) return; const t = setTimeout(() => setResendIn(resendIn - 1), 1000); return () => clearTimeout(t); }, [resendIn]);

  const verify = async (addr: string, token: string) => {
    if (!supabase) return;
    setBusy(true); setErr(null);
    const r = await supabase.auth.verifyOtp({ email: addr.trim().toLowerCase(), token: token.trim(), type: 'email' });
    setBusy(false);
    if (r.error) { setErr(/expired|invalid/i.test(r.error.message) ? 'That code is wrong or has expired. Ask for a new one.' : r.error.message); setStep('code'); return; }
    setParams({}, { replace: true });
    navigate(next, { replace: true });
  };

  // the email's button, or the Google return: the code is in the address
  useEffect(() => {
    const e = params.get('email'); const c = params.get('code');
    // back from Google (or the email's button): sign in quietly, never show the code form with the digits filled in,
    // and take the one-time code out of the address at once (owner report 2026-10-24); a failure shows the code form
    if (supabase && e && c) { setEmail(e); setCode(c); setStep('auto'); setParams({}, { replace: true }); void verify(e, c); }
  }, [supabase]);

  const send = async (ev?: React.FormEvent) => {
    ev?.preventDefault(); setErr(null);
    // "a@b" passes the browser's check and was refused by the server
    if (!/^[^@\s]+@[^@\s]+\.[a-z]{2,}$/i.test(email.trim())) { setErr('Enter a valid email address, like you@company.com.'); return; }
    setBusy(true);
    try {
      if (cfg?.email_code) await api('/public/auth/email', { method: 'POST', body: { email } });
      else if (supabase) { const r = await supabase.auth.signInWithOtp({ email }); if (r.error) throw new Error(r.error.message); }
      setStep('code'); setResendIn(30); setCode('');
    } catch (x) { setErr((x as Error).message); }
    setBusy(false);
  };

  return (
    <main className="auth" id="main">
      <Logo className="auth-logo" />
      <h1>{fromAssessment ? 'Save your assessment' : signup || arriving ? 'Create your Outlevo account' : 'Sign in to Outlevo'}</h1>
      {pickedPlan() && !fromAssessment ? <p className="auth-note">You picked {pickedPlan() === 'growth' ? 'Growth' : 'Builder'}. Create your account, then choose it on Billing.</p> : null}
      {fromAssessment ? <p className="auth-note">Your free ICP assessment is kept: sign in or sign up with your work email and it becomes your ICP draft, with its segments and markets.</p> : null}
      {step === 'auto' ? (
        <p className="auth-wait" role="status" aria-live="polite"><span className="spinner" aria-hidden="true" /> Signing you in…</p>
      ) : step === 'email' ? (
        <>
          {cfg?.google ? (
            <>
              <a className="btn btn-google" href={`/public/auth/google/start?next=${encodeURIComponent(next)}`}>
                <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true"><path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9.1 3.6l6.8-6.8C35.8 2.4 30.3 0 24 0 14.6 0 6.6 5.4 2.7 13.3l7.9 6.1C12.5 13.6 17.8 9.5 24 9.5z"/><path fill="#4285F4" d="M46.1 24.5c0-1.6-.1-3.1-.4-4.5H24v9h12.4c-.5 2.9-2.2 5.3-4.6 6.9l7.4 5.7c4.3-4 6.9-9.9 6.9-17.1z"/><path fill="#FBBC05" d="M10.6 28.6c-.5-1.4-.8-3-.8-4.6s.3-3.2.8-4.6l-7.9-6.1C1 16.6 0 20.2 0 24s1 7.4 2.7 10.7l7.9-6.1z"/><path fill="#34A853" d="M24 48c6.5 0 11.9-2.1 15.9-5.8l-7.4-5.7c-2.1 1.4-4.8 2.3-8.5 2.3-6.2 0-11.5-4.1-13.4-9.9l-7.9 6.1C6.6 42.6 14.6 48 24 48z"/></svg>
                Continue with Google
              </a>
              <p className="auth-or"><span>or</span></p>
            </>
          ) : null}
          <form onSubmit={send} className="form">
            <div className="field"><label htmlFor="email">Work email</label><input id="email" type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" /></div>
            {err ? <p className="error" role="alert">{err}</p> : null}
            <button type="submit" className="btn btn-primary" disabled={busy || !cfg}>{busy ? 'Sending…' : 'Email me a sign-in code'}</button>
          </form>
          <p className="muted small">No password to remember. {signup || arriving ? 'We email you a code; it works for a new account and for a returning one.' : 'New here? The same code creates your account.'}</p>
        </>
      ) : (
        <form onSubmit={(e) => { e.preventDefault(); void verify(email, code); }} className="form">
          <p role="status">We sent a sign-in code to <strong>{email}</strong>. It works once and expires in one hour.</p>
          <div className="field"><label htmlFor="code">Sign-in code</label>
            <input id="code" className="code-input" inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]{6,10}" maxLength={10} required autoFocus
              value={code} onChange={(e) => { const v = e.target.value.replace(/\D/g, ''); const pasted = v.length - code.length > 1; setCode(v); if (pasted && v.length >= 6 && !busy) void verify(email, v); }} /></div>
          {err ? <p className="error" role="alert">{err}</p> : null}
          <button type="submit" className="btn btn-primary" disabled={busy || code.length < 6}>{busy ? 'Checking…' : 'Sign in'}</button>
          <div className="auth-links">
            <button type="button" className="link" disabled={resendIn > 0 || busy} onClick={() => void send()}>{resendIn > 0 ? `Resend in ${resendIn}s` : 'Send a new code'}</button>
            <button type="button" className="link" onClick={() => { setStep('email'); setErr(null); setCode(''); }}>Use a different address</button>
          </div>
        </form>
      )}
      <p className="muted small">With a personal address (Gmail, Outlook…) you get a practice workspace with sample companies; use your work email to find real buyers.</p>
      <p className="muted small">Building on Outlevo? <a href="https://developers.outlevo.com">Developer docs</a></p>
    </main>
  );
}

/** Enrol a TOTP factor, or re-verify to open the 12-hour approval window. */
export function MfaPage() {
  const { supabase, session, me, mfaAgeMs, refresh } = useAuth();
  const [factor, setFactor] = useState<{ id: string; qr?: string; secret?: string } | null>(null);
  const [checked, setChecked] = useState(false);
  const [code, setCode] = useState('');
  const [err, setErr] = useState<unknown>(null);
  const [again, setAgain] = useState(false);
  useEffect(() => { document.title = 'Outlevo · Two-factor'; }, []);
  useEffect(() => {
    if (!supabase) return;
    supabase.auth.mfa.listFactors().then(({ data }) => {
      const f = data?.totp?.find((x) => x.status === 'verified');
      if (f) setFactor({ id: f.id });
      setChecked(true);
    });
  }, [supabase]);
  const next = (() => { const n = new URLSearchParams(location.search).get('next'); return n && n.startsWith('/') && !n.startsWith('//') ? n : '/approvals'; })();
  // the entry in the authenticator app reads "Outlevo: <email>" (without an issuer Supabase used the project's Site URL)
  const enroll = async () => {
    setErr(null);
    const r = await supabase!.auth.mfa.enroll({ factorType: 'totp', issuer: 'Outlevo', friendlyName: `Outlevo ${new Date().toISOString().slice(0, 10)}` });
    if (r.error) return setErr({ message: r.error.message });
    setFactor({ id: r.data.id, qr: r.data.totp.qr_code, secret: r.data.totp.secret });
  };
  const verify = async (e: React.FormEvent) => {
    e.preventDefault(); setErr(null);
    const r = await supabase!.auth.mfa.challengeAndVerify({ factorId: factor!.id, code: code.replace(/\s/g, '') });
    if (r.error) return setErr({ message: r.error.message });
    await supabase!.auth.refreshSession();
    await refresh();
    setCode(''); setAgain(false);
    // sent here from an approval ("Verify now"): go straight back to it
    if (new URLSearchParams(location.search).get('next')) location.assign(next);
  };
  const fresh = mfaAgeMs !== null && mfaAgeMs <= MFA_WINDOW_MS;
  const until = fresh ? new Date(Date.now() + MFA_WINDOW_MS - mfaAgeMs!).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : null;
  const email = session?.user?.email;
  return (
    <main className="page" id="main">
      <h1>Two-factor authentication</h1>
      <p className="muted">Approvals (policies, field mappings, sending) need a second factor in the last 12 hours.{email ? <> Signed in as <strong>{email}</strong>{me ? <>, workspace <strong>{me.org.name}</strong></> : null}.</> : null}</p>
      {fresh && !factor?.qr && !again ? (
        <div className="notice notice-ok" role="status" style={{ maxWidth: 520 }}>
          <p>You are verified. Approvals are open until {until}.</p>
          <p className="form-actions"><a className="btn btn-primary" href={next}>Go to approvals</a> <button type="button" className="btn" onClick={() => setAgain(true)}>Verify again</button></p>
        </div>
      ) : !checked ? null : !factor ? (
        <div style={{ maxWidth: 520 }}>
          <p>Set up an authenticator app once (Google Authenticator, 1Password, Authy or similar). After that you enter its 6-digit code here when you approve something.</p>
          <button type="button" className="btn btn-primary" onClick={enroll}>Set up an authenticator app</button>
        </div>
      ) : (
        <form onSubmit={verify} className="form" style={{ maxWidth: 360 }}>
          {factor.qr ? (
            <>
              <p>1. Scan this code with your authenticator app. The entry is named <strong>Outlevo</strong>.</p>
              <img src={factor.qr} alt="QR code for your authenticator app" width={180} height={180} />
              <p className="small muted">Can't scan it? Enter this key in the app: <code>{factor.secret}</code></p>
              <p>2. Enter the 6-digit code the app shows.</p>
            </>
          ) : <p>Enter the 6-digit code from your authenticator app (the <strong>Outlevo</strong> entry).</p>}
          <div className="field"><label htmlFor="code">6-digit code</label><input id="code" inputMode="numeric" autoComplete="one-time-code" pattern="[0-9 ]{6,7}" required value={code} onChange={(e) => setCode(e.target.value)} /></div>
          {err ? <ErrorBox error={err} /> : null}
          <button type="submit" className="btn btn-primary">Verify</button>
        </form>
      )}
    </main>
  );
}

/** First-touch attribution set by outlevo.com (site.js): the olv_utm cookie on .outlevo.com, else this page's own UTM tags. */
function signupUtm(): Record<string, string> | undefined {
  try {
    const c = document.cookie.match(/(?:^|; )olv_utm=([^;]+)/)?.[1];
    if (c) return JSON.parse(decodeURIComponent(c)) as Record<string, string>;
  } catch { /* unreadable cookie: fall back to the URL */ }
  const q = new URLSearchParams(location.search);
  const out = Object.fromEntries(['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'gclid', 'fbclid', 'twclid', 'rdt_cid', 'li_fat_id']
    .flatMap((k) => (q.get(k) ? [[k, q.get(k)!.slice(0, 200)]] : [])));
  return Object.keys(out).length ? out : undefined;
}

type OwnAssessment = { id: string; domain: string; company: string | null; country: string | null; segments: number };
const PENDING = 'olv_assessment';
const PENDING_MARKETS = 'olv_assessment_markets';

export function CreateOrg() {
  const { refresh, supabase, session, me } = useAuth();
  const [err, setErr] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { document.title = 'Outlevo · Create your workspace'; }, []);
  const nav = useNavigate();
  const emailDomain = (session?.user.email ?? '').split('@')[1] ?? '';
  // a free-mail domain is never the company's: leave the field for the person to fill in
  const workDomain = /^(gmail|googlemail|yahoo|outlook|hotmail|icloud|me|proton|protonmail|aol|gmx|mail|yandex)\./.test(emailDomain) ? '' : emailDomain;
  const [name, setName] = useState('');
  const [domain, setDomain] = useState(workDomain);
  const [country, setCountry] = useState('US');
  const [touched, setTouched] = useState(false);
  // the person's own assessment (the link's, or the latest of their address or work domain): it fills this form and becomes the ICP
  const [own, setOwn] = useState<OwnAssessment | null>(null);
  useEffect(() => {
    if (me) return; // a second workspace is not an assessment's
    let id = ''; try { id = localStorage.getItem(PENDING) ?? ''; } catch { /* private mode */ }
    api<{ assessment: OwnAssessment | null }>(`/portal/assessments/mine${/^[0-9a-f-]{36}$/i.test(id) ? `?id=${id}` : ''}`).then((r) => setOwn(r.assessment)).catch(() => undefined);
  }, [me]);
  useEffect(() => {
    if (!own || touched) return;
    if (own.company) setName(own.company);
    setDomain(own.domain);
    const c = countryCode(own.country);
    if (c) setCountry(c);
  }, [own, touched]);
  const submit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault(); setErr(null); setBusy(true);
    try {
      // refer and earn: the code from an outlevo.com/?ref= link (a cookie on .outlevo.com) or this page's ?ref=
      const ref = new URLSearchParams(location.search).get('ref') ?? document.cookie.match(/(?:^|; )olv_ref=([a-z0-9]{6,16})/)?.[1];
      const utm = signupUtm();
      // the admin journey: the outlevo.com visits before sign-up (not sent under Global Privacy Control or Do Not Track)
      const vid = visitorId();
      const r = await api<{ org: { id: string } }>('/portal/orgs', { method: 'POST', body: { name: name.trim(), primary_domain: domain.trim(), billing_country: country, ...(ref ? { referral_code: ref } : {}), ...(utm ? { signup_utm: utm } : {}), ...(vid ? { visitor_id: vid } : {}) } });
      // a second workspace: make it the active one (the first one is chosen by the auth hook anyway)
      if (me) await api('/portal/switch-org', { method: 'POST', body: { org_id: r.org.id } });
      await supabase!.auth.refreshSession(); // the auth hook adds org_id to the new token
      // the assessment becomes the ICP draft now, so the first screen is the ICP with its segments; if this fails the
      // app retries it (App.tsx AssessmentClaim keeps the id until it is taken)
      let landing = '/';
      const pending = own && !me ? own.id : null;
      if (pending) {
        try {
          localStorage.setItem(PENDING, pending);
          const m = localStorage.getItem(PENDING_MARKETS);
          await api(`/portal/assessments/${pending}/claim`, { method: 'POST', body: m === null ? {} : { markets: m ? m.split(',') : [] } });
          localStorage.removeItem(PENDING); localStorage.removeItem(PENDING_MARKETS);
          landing = '/icp';
        } catch { /* the app tries again once it is open */ }
      }
      await refresh();
      nav(landing);
    } catch (x) { setErr(x); }
    setBusy(false);
  };
  return (
    <main className="auth" id="main">
      <h1>{me ? 'New workspace' : 'Create your workspace'}</h1>
      <p className="muted">{me ? 'One workspace per website you sell for, each with its own ICP, accounts, credits and test mode.' : 'Or ask a colleague to invite you to an existing one.'}</p>
      {own && !me ? <p className="auth-note" role="status">Your free assessment of {own.domain} is ready{own.segments ? ` with ${own.segments} segment${own.segments === 1 ? '' : 's'}` : ''}. It becomes your first ICP once the workspace exists.</p> : null}
      <form onSubmit={submit} className="form">
        <div className="field"><label htmlFor="n">Company name</label><input id="n" name="name" required maxLength={200} value={name} onChange={(e) => { setTouched(true); setName(e.target.value); }} /></div>
        <div className="field"><label htmlFor="d">Company domain</label><input id="d" name="domain" required value={domain} onChange={(e) => { setTouched(true); setDomain(e.target.value); }} placeholder="example.com" autoCapitalize="none" spellCheck={false} /></div>
        <div className="field"><label htmlFor="c">Billing country</label>
          <select id="c" name="country" required value={country} onChange={(e) => { setTouched(true); setCountry(e.target.value); }} autoComplete="country">
            {BILLING_COUNTRIES.map(([code, label]) => <option key={code} value={code}>{label}</option>)}
          </select></div>
        {err ? <ErrorBox error={err} /> : null}
        <button type="submit" className="btn btn-primary" disabled={busy}>{busy ? 'Creating…' : 'Create workspace'}</button>
      </form>
    </main>
  );
}

export function InviteAccept() {
  const { session, supabase, refresh } = useAuth();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [err, setErr] = useState<unknown>(null);
  const token = params.get('token') ?? '';
  useEffect(() => { document.title = 'Outlevo · Accept invitation'; }, []);
  if (!session) return <main className="auth"><h1>Accept your invitation</h1><p>Sign in with the address the invitation was sent to, then open the link again.</p><Login /></main>;
  return (
    <main className="auth" id="main">
      <h1>Accept your invitation</h1>
      <p>Signed in as {session.user.email}.</p>
      {err ? <ErrorBox error={err} /> : null}
      <button type="button" className="btn btn-primary" onClick={async () => {
        try { await api('/portal/invitations/accept', { method: 'POST', body: { token } }); await supabase!.auth.refreshSession(); await refresh(); nav('/'); } catch (x) { setErr(x); }
      }}>Join the workspace</button>
    </main>
  );
}
